import { NotificationItem } from "@/entities/notification/model/types";

export interface NotificationGroup {
    key: string;
    title: string;
    type?: string;
    items: NotificationItem[];
    latest: NotificationItem;
    unreadCount: number;
}

type DayBucket = "today" | "yesterday" | "older";

export interface NotificationSection {
    bucket: DayBucket;
    groups: NotificationGroup[];
}

const BUCKET_ORDER: DayBucket[] = ["today", "yesterday", "older"];

export function isChat(type?: string): boolean {
    return type === "CHAT_MESSAGE";
}

function bucketOf(dateStr: string, now: Date = new Date()): DayBucket {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return "older";

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const time = date.getTime();
    if (time >= startOfToday) return "today";
    if (time >= startOfToday - 86400000) return "yesterday";
    return "older";
}

/**
 * Agrupa notificações que partilham o mesmo `type` + `title`. Para mensagens de
 * chat o `title` é o nome do remetente, pelo que mensagens da mesma pessoa ficam
 * num único grupo.
 */
function buildGroups(items: NotificationItem[]): NotificationGroup[] {
    const map = new Map<string, NotificationItem[]>();
    const order: string[] = [];

    for (const item of items) {
        const key = `${item.type ?? ""}||${item.title}`;
        if (!map.has(key)) {
            map.set(key, []);
            order.push(key);
        }
        map.get(key)!.push(item);
    }

    return order.map((key) => {
        const groupItems = map.get(key)!;
        return {
            key,
            title: groupItems[0].title,
            type: groupItems[0].type,
            items: groupItems,
            latest: groupItems[0],
            unreadCount: groupItems.filter((i) => !i.read).length,
        };
    });
}

/** Agrupa por tipo/remetente e depois reparte por dia, mantendo a ordem recebida. */
export function buildSections(items: NotificationItem[], now: Date = new Date()): NotificationSection[] {
    const groups = buildGroups(items);
    const byBucket = new Map<DayBucket, NotificationGroup[]>();

    for (const group of groups) {
        const bucket = bucketOf(group.latest.createdAt, now);
        if (!byBucket.has(bucket)) byBucket.set(bucket, []);
        byBucket.get(bucket)!.push(group);
    }

    return BUCKET_ORDER.filter((bucket) => byBucket.has(bucket)).map((bucket) => ({
        bucket,
        groups: byBucket.get(bucket)!,
    }));
}

export interface RelativeTime {
    key: string;
    count?: number;
}

/** Devolve chave de tradução em vez de texto, para o painel resolver com i18n. */
export function relativeTime(dateStr: string, now: Date = new Date()): RelativeTime {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return { key: "nav.notifications.timeNow" };

    const minutes = Math.floor((now.getTime() - date.getTime()) / 60000);
    if (minutes < 1) return { key: "nav.notifications.timeNow" };
    if (minutes < 60) return { key: "nav.notifications.timeMinutes", count: minutes };

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return { key: "nav.notifications.timeHours", count: hours };

    return { key: "nav.notifications.timeDays", count: Math.floor(hours / 24) };
}
